import React, { useState } from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, ScoreCell, ProvenanceFooter, ProvenanceBadge } from '../components/ui/kits.jsx';
import { fmt, fmtInt, bandColor, CRIT_COLOR } from '../lib/format.js';
import { ChevronRight } from 'lucide-react';

export default function ParameterScoring() {
  const { data } = useData();
  const [openIdx, setOpenIdx] = useState(new Set());
  if (!data) return null;
  const { matrix, meta, heatmap } = data;

  const toolCols = ['NRM', 'Health', 'SDLE', 'POE'];
  const toolLabels = { NRM: 'NRM', Health: 'H&H', SDLE: 'SDLE', POE: 'PoE' };
  const toolOverall = Object.fromEntries(heatmap.map((h) => [h.tool, h.overall]));

  const toggle = (i) => {
    const n = new Set(openIdx);
    if (n.has(i)) n.delete(i); else n.add(i);
    setOpenIdx(n);
  };

  const totalSubs = matrix.reduce((a, m) => a + (m.subs?.length || 0), 0);

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-param">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <KpiCard testid="kpi-responses" label="Total Responses" value={fmtInt(meta.totalResponses)} sub="Beneficiary surveys logged" />
        <KpiCard testid="kpi-oecd-crit" label="OECD Criteria" value="7" sub={`${meta.realCriteria.length} real · ${meta.derivedCriteria.length} modelled`} />
        <KpiCard testid="kpi-subindicators" label="Sub-indicators" value={fmtInt(totalSubs)} sub="Expandable per criterion" />
        <KpiCard testid="kpi-po-hero" hero label="Programme Overall" value={fmt(meta.programmeOverall, 2)} band={data.kpi.programmeBand} />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Legend />
            <ProvenanceBadge kind="live" note="Rel/Eff/Sus" />
            <ProvenanceBadge kind="model" note="Coh/Effs/Imp/Brd rows" />
          </div>
        }>Weighted Scoring Matrix · OECD × Thematic tool</SectionTitle>
        <div className="scroll-x">
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 720 }}>
            <thead>
              <tr>
                <th style={thStyle}>Criterion</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
                {toolCols.map((t) => <th key={t} style={{ ...thStyle, textAlign: 'center' }}>{toolLabels[t]}</th>)}
                <th style={{ ...thStyle, textAlign: 'center' }}>Overall</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row, i) => {
                const open = openIdx.has(i);
                return (
                  <React.Fragment key={row.criterion}>
                    <tr onClick={() => toggle(i)} style={{ cursor: 'pointer', background: open ? 'var(--surface-2)' : 'transparent' }} data-testid={`matrix-row-${row.criterion}`}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ChevronRight size={14} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s', color: 'var(--ink-4)' }} />
                          <span style={{ fontWeight: 700, color: CRIT_COLOR[row.criterion] }}>{row.criterion}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--ink-3)' }} className="tabular">{(row.weight * 100).toFixed(0)}%</td>
                      {toolCols.map((t) => {
                        const c = row.cells[t];
                        return (
                          <td key={t} style={{ ...tdStyle, textAlign: 'center' }}>
                            {c && c.score !== null ? (
                              <>
                                <ScoreCell score={c.score} />
                                <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 2 }} className="tabular">n={fmtInt(c.n)}</div>
                              </>
                            ) : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                          </td>
                        );
                      })}
                      <td style={{ ...tdStyle, textAlign: 'center' }}><ScoreCell score={row.overall} /></td>
                    </tr>
                    {open && row.subs?.map((sub, j) => (
                      <tr key={`${i}-${j}`} style={{ background: '#fbfcfe' }}>
                        <td style={{ ...tdStyle, paddingLeft: 40, fontSize: 12, color: 'var(--ink-2)' }}>{sub.name}</td>
                        <td style={tdStyle} />
                        {toolCols.map((t) => {
                          const c = sub.cells[t];
                          return (
                            <td key={t} style={{ ...tdStyle, textAlign: 'center' }}>
                              {c && c.score !== null ? (
                                <>
                                  <ScoreCell score={c.score} />
                                  <div style={{ fontSize: 10, color: 'var(--ink-4)' }} className="tabular">n={fmtInt(c.n)}</div>
                                </>
                              ) : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                            </td>
                          );
                        })}
                        <td style={{ ...tdStyle, textAlign: 'center' }}><ScoreCell score={sub.overall} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              <tr style={{ background: 'var(--navy)', color: '#fff' }}>
                <td style={{ ...tdStyle, fontWeight: 800 }}>Programme total</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>100%</td>
                {toolCols.map((t) => (
                  <td key={t} style={{ ...tdStyle, textAlign: 'center', fontWeight: 800 }} className="tabular">{fmt(toolOverall[t], 2)}</td>
                ))}
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 800, fontSize: 14 }} className="tabular">{fmt(meta.programmeOverall, 2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <ProvenanceFooter text={`Real criteria (computed from ${fmtInt(meta.totalResponses)} responses): ${meta.realCriteria.join(', ')}. Modelled criteria (placeholders pending re-export of computed score columns): ${meta.derivedCriteria.join(', ')}.`} />
    </div>
  );
}

const thStyle = { fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em', padding: '10px 12px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid var(--line-2)', fontSize: 12.5 };

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => (
        <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600 }}>
          <span style={{ width: 10, height: 10, background: bandColor(b), borderRadius: 3 }} />{b}
        </div>
      ))}
    </div>
  );
}
